import { useCallback, useEffect, useState } from 'react';
import { Loader2, Printer, RefreshCw, Search, Store } from 'lucide-react';
import {
  GiftcardListItem,
  PAYMENT_METHOD_LABELS,
  PaymentMethod,
} from '../../../interfaces/giftcard.interface';
import {
  giftcardErrorMessage,
  listGiftcards,
} from '../../../services/giftcards.service';
import { printGiftcard } from '../utils/printGiftcard';

const formatDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString('fr-FR') : '—';

const paymentLabel = (method: string | null) =>
  method && method in PAYMENT_METHOD_LABELS
    ? PAYMENT_METHOD_LABELS[method as PaymentMethod]
    : '—';

const StatusBadge = ({ item }: { item: GiftcardListItem }) => {
  const expired =
    !item.used &&
    !!item.expiresAt &&
    new Date(item.expiresAt).getTime() < Date.now();

  if (item.used) {
    return (
      <span className="rounded-full bg-gray-100 px-2 py-1 text-xs font-medium text-gray-600">
        Utilisé
      </span>
    );
  }
  if (expired) {
    return (
      <span className="rounded-full bg-[#BB0F33]/10 px-2 py-1 text-xs font-medium text-[#BB0F33]">
        Expiré
      </span>
    );
  }
  return (
    <span className="rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-700">
      Valide
    </span>
  );
};

// On affiche les N plus récents par défaut ; « Afficher plus » en révèle N de plus.
const PAGE_SIZE = 10;
// On charge un lot plus large que l'affichage initial pour que « Afficher plus »
// n'ait pas à refaire un appel réseau tant qu'on reste sous ce plafond.
const FETCH_LIMIT = 100;

interface Props {
  /** Change à chaque bon créé : déclenche un rafraîchissement de la liste. */
  refreshToken: number;
}

const GiftcardHistory = ({ refreshToken }: Props) => {
  const [items, setItems] = useState<GiftcardListItem[]>([]);
  const [search, setSearch] = useState('');
  const [submittedSearch, setSubmittedSearch] = useState('');
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchItems = useCallback(async (term: string) => {
    setLoading(true);
    setError(null);
    try {
      setItems(
        await listGiftcards({ search: term || undefined, limit: FETCH_LIMIT })
      );
      setVisibleCount(PAGE_SIZE); // tout rechargement repart des 10 plus récents
    } catch (err) {
      console.error('❌ Erreur chargement des bons :', err);
      setError(giftcardErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchItems(submittedSearch);
  }, [fetchItems, submittedSearch, refreshToken]);

  // En recherche, on montre tous les résultats (ils sont déjà ciblés par le code).
  const isSearching = !!submittedSearch;
  const visibleItems = isSearching ? items : items.slice(0, visibleCount);
  const hasMore = !isSearching && items.length > visibleCount;
  const isExpanded = !isSearching && visibleCount > PAGE_SIZE;

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmittedSearch(search.trim());
  };

  return (
    <div className="rounded-2xl border border-[#623413]/10 bg-white shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-6 py-4">
        <h3 className="font-semibold text-[#623413]">Bons émis</h3>

        <div className="flex items-center gap-2">
          <form onSubmit={handleSearch} className="flex items-center gap-2">
            <div className="relative">
              <Search
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value.toUpperCase())}
                placeholder="Rechercher un code"
                className="w-52 rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm uppercase focus:outline-none focus:ring-2 focus:ring-[#623413]/40"
              />
            </div>
            <button
              type="submit"
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition"
            >
              Chercher
            </button>
          </form>

          <button
            type="button"
            onClick={() => fetchItems(submittedSearch)}
            className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 transition"
            title="Rafraîchir"
          >
            <RefreshCw size={16} />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center gap-2 py-12 text-gray-500">
          <Loader2 size={18} className="animate-spin" />
          Chargement...
        </div>
      ) : error ? (
        <p className="px-6 py-10 text-center text-sm text-[#BB0F33]">{error}</p>
      ) : items.length === 0 ? (
        <p className="px-6 py-10 text-center text-sm text-gray-500">
          {submittedSearch
            ? `Aucun bon ne correspond au code « ${submittedSearch} ».`
            : 'Aucun bon pour le moment.'}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 text-left text-xs uppercase tracking-wide text-gray-500">
                <th className="px-6 py-3 font-medium">Code</th>
                <th className="px-4 py-3 font-medium">Montant</th>
                <th className="px-4 py-3 font-medium">Bénéficiaire</th>
                <th className="px-4 py-3 font-medium">Origine</th>
                <th className="px-4 py-3 font-medium">Statut</th>
                <th className="px-4 py-3 font-medium">Émis le</th>
                <th className="px-4 py-3 font-medium">Expire le</th>
                <th className="px-6 py-3" />
              </tr>
            </thead>
            <tbody>
              {visibleItems.map((item) => (
                <tr
                  key={item.id}
                  className="border-b border-gray-50 last:border-0 hover:bg-gray-50/60"
                >
                  <td className="px-6 py-3 font-mono font-semibold tracking-wider text-[#623413]">
                    {item.code}
                  </td>
                  <td className="px-4 py-3 font-medium text-gray-800">
                    {item.value} €
                  </td>
                  <td className="px-4 py-3 text-gray-700">{item.to || '—'}</td>
                  <td className="px-4 py-3 text-gray-600">
                    {item.source === 'manual' ? (
                      <span
                        className="inline-flex items-center gap-1.5"
                        title={paymentLabel(item.paymentMethod)}
                      >
                        <Store size={14} className="text-[#623413]" />
                        Boutique
                      </span>
                    ) : (
                      'Site (Stripe)'
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge item={item} />
                  </td>
                  <td className="px-4 py-3 text-gray-500">
                    {formatDate(item.createdAt)}
                  </td>
                  <td className="px-4 py-3 text-gray-500">
                    {formatDate(item.expiresAt)}
                  </td>
                  <td className="px-6 py-3 text-right">
                    {item.imageUrl && (
                      <button
                        type="button"
                        onClick={() => printGiftcard(item.imageUrl!)}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50 transition"
                        title="Réimprimer ce bon"
                      >
                        <Printer size={14} />
                        Imprimer
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {!isSearching && (hasMore || isExpanded) && (
            <div className="flex flex-wrap items-center justify-between gap-2 border-t border-gray-100 px-6 py-3">
              <span className="text-xs text-gray-400">
                {visibleItems.length} sur {items.length} affiché
                {items.length > 1 ? 's' : ''}
              </span>
              <div className="flex items-center gap-2">
                {isExpanded && (
                  <button
                    type="button"
                    onClick={() => setVisibleCount(PAGE_SIZE)}
                    className="rounded-lg px-3 py-1.5 text-sm text-gray-500 hover:bg-gray-100 transition"
                  >
                    Réduire
                  </button>
                )}
                {hasMore && (
                  <button
                    type="button"
                    onClick={() =>
                      setVisibleCount((count) => count + PAGE_SIZE)
                    }
                    className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 transition"
                  >
                    Afficher plus ({items.length - visibleCount} restant
                    {items.length - visibleCount > 1 ? 's' : ''})
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default GiftcardHistory;
